import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Send, 
  Users, 
  Mail, 
  FileText, 
  Clock, 
  CheckCircle2,
  AlertCircle,
  History,
  Paperclip
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr, de, enUS } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamCommunication() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const locale = language === 'de' ? de : language === 'en' ? enUS : fr;

  const [team, setTeam] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  // Form state
  const [recipients, setRecipients] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [attachPlanning, setAttachPlanning] = useState(false);
  const [planningWeeks, setPlanningWeeks] = useState('1');

  const isManager = user?.role === 'manager' || user?.role === 'directeur';

  const fetchTeam = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/team`);
      setTeam(res.data);
    } catch (err) {
      console.error('Error fetching team:', err);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/team/communication-history?limit=10`);
      setHistory(res.data);
    } catch (err) {
      console.error('Error fetching history:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeam();
    fetchHistory();
  }, [fetchTeam, fetchHistory]);

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setRecipients(team.map(m => m.id));
    } else {
      setRecipients([]);
    }
  };

  const handleRecipientChange = (userId, checked) => {
    if (checked) {
      setRecipients([...recipients, userId]);
    } else {
      setRecipients(recipients.filter(id => id !== userId));
      setSelectAll(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (recipients.length === 0 && !selectAll) {
      toast.error(t('selectAtLeastOneRecipient'));
      return;
    }
    
    if (!subject.trim()) {
      toast.error(t('subjectRequired'));
      return;
    }
    
    if (!message.trim()) {
      toast.error(t('messageRequired'));
      return;
    }

    setSending(true);
    
    try {
      const payload = {
        recipients: selectAll ? ['all'] : recipients,
        subject: subject.trim(),
        message: message.trim(),
        attach_planning: attachPlanning,
        planning_weeks: parseInt(planningWeeks)
      };

      const res = await axios.post(`${API}/team/communicate`, payload);
      
      toast.success(`${t('messageSentTo')} ${res.data.recipients_count} ${t('recipients')}`);
      
      // Reset form
      setRecipients([]);
      setSelectAll(false);
      setSubject('');
      setMessage('');
      setAttachPlanning(false);
      setPlanningWeeks('1');
      
      // Refresh history
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('sendError'));
    } finally {
      setSending(false);
    }
  };

  if (!isManager) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">{t('accessDenied')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-communication-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">{t('teamCommunication').toUpperCase()}</h1>
        <p className="text-muted-foreground text-sm">{t('sendMessageToTeam')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Message Form */}
        <div className="lg:col-span-2">
          <div className="bg-card border rounded-lg p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Recipients */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  {t('recipients')}
                </Label>
                
                <div className="flex items-center space-x-2 mb-3">
                  <Checkbox
                    id="select-all"
                    checked={selectAll}
                    onCheckedChange={handleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                  <label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
                    {t('selectAllTeam')} ({team.length})
                  </label>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-3 bg-muted/30 rounded-lg">
                  {team.map(member => (
                    <div key={member.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`recipient-${member.id}`}
                        checked={selectAll || recipients.includes(member.id)}
                        onCheckedChange={(checked) => handleRecipientChange(member.id, checked)}
                        disabled={selectAll}
                      />
                      <label 
                        htmlFor={`recipient-${member.id}`} 
                        className="text-sm cursor-pointer flex items-center gap-2"
                      >
                        <span 
                          className="w-2 h-2 rounded-full" 
                          style={{ backgroundColor: member.color || '#3B82F6' }}
                        />
                        {member.first_name} {member.last_name?.charAt(0)}.
                      </label>
                    </div>
                  ))}
                </div>
                
                {recipients.length > 0 && !selectAll && (
                  <p className="text-xs text-muted-foreground">
                    {recipients.length} {t('recipientsSelected')}
                  </p>
                )}
              </div>

              {/* Subject */}
              <div className="space-y-2">
                <Label htmlFor="subject" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  {t('subject')}
                </Label>
                <Input
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={t('enterSubject')}
                  data-testid="subject-input"
                />
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  {t('message')}
                </Label>
                <Textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('enterMessage')}
                  rows={6}
                  data-testid="message-textarea"
                />
              </div>

              {/* Attach Planning */}
              <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="attach-planning"
                    checked={attachPlanning}
                    onCheckedChange={setAttachPlanning}
                    data-testid="attach-planning-checkbox"
                  />
                  <label htmlFor="attach-planning" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                    <Paperclip className="h-4 w-4" />
                    {t('attachPlanningPDF')}
                  </label>
                </div>

                {attachPlanning && (
                  <div className="ml-6 space-y-2">
                    <Label htmlFor="planning-weeks" className="text-sm">
                      {t('planningDuration')}
                    </Label>
                    <Select value={planningWeeks} onValueChange={setPlanningWeeks}>
                      <SelectTrigger className="w-48" data-testid="planning-weeks-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 {t('week')}</SelectItem>
                        <SelectItem value="2">2 {t('weeks')}</SelectItem>
                        <SelectItem value="3">3 {t('weeks')}</SelectItem>
                        <SelectItem value="4">4 {t('weeks')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Submit */}
              <Button 
                type="submit" 
                disabled={sending} 
                className="w-full"
                data-testid="send-button"
              >
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    {t('sending')}...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    {t('sendMessage')}
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>

        {/* History */}
        <div className="lg:col-span-1">
          <div className="bg-card border rounded-lg p-6">
            <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <History className="h-5 w-5" />
              {t('messageHistory')}
            </h2>

            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                {t('noMessagesSent')}
              </p>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {history.map((comm) => (
                  <div 
                    key={comm.id} 
                    className="p-3 bg-muted/30 rounded-lg border border-border/50 hover:border-border transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-medium text-sm line-clamp-1">{comm.subject}</h3>
                      {comm.had_attachment && (
                        <Paperclip className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                      )}
                    </div>
                    
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {comm.message}
                    </p>
                    
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/30">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Users className="h-3 w-3" />
                        {comm.sent_count}/{comm.recipients_count}
                      </div>
                      
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {format(parseISO(comm.sent_at), 'dd MMM HH:mm', { locale })}
                      </div>
                    </div>

                    {comm.failed_emails && comm.failed_emails.length > 0 && (
                      <div className="flex items-center gap-1 text-xs text-amber-500 mt-1">
                        <AlertCircle className="h-3 w-3" />
                        {comm.failed_emails.length} {t('failed')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
